import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";
import { Document } from "@/types/models";

const EVIDENCE_BASE_API_URL =
  getEnv("VITE_EVIDENCE_BASE_API_URL") ||
  "https://jca-heor-service-evidence-base.bluegrass-b6db7524.eastus.azurecontainerapps.io";

// --- API response type (snake_case from backend) ---

export interface DocumentApiResponse {
  id: string;
  filename: string | null;
  title: string | null;
  authors: string | null;
  year: string | null;
  description: string | null;
  source_type: string | null;
  organization: string | null;
  country: string | null;
  evidence_type: string | null;
  date_relevance: string | null;
  url: string | null;
  language: string | null;
  ownership: string | null;
  tags: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function toDocument(r: DocumentApiResponse): Document {
  return {
    id: r.id,
    filename: r.filename || "",
    title: r.title || r.filename || "",
    author: r.authors || "",
    description: r.description || "",
    tags: r.tags ? r.tags.split(",").map((t) => t.trim()) : [],
    sourceType: r.source_type || "",
    sourceOrganisation: r.organization || "",
    country: r.country || "",
    year: r.year || "",
    evidenceType: r.evidence_type || "",
    dateRelevance: r.date_relevance || "",
    url: r.url || "",
    language: r.language || "",
    ownership: r.ownership || "",
    createdAt: r.created_at,
  };
}

// --- Fetch documents ---

export async function fetchDocuments(
  getAccessToken: () => Promise<string | null>
): Promise<Document[]> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `Fetch failed: ${response.status}`);
  }

  const data: DocumentApiResponse[] = await response.json();
  return data.map(toDocument);
}

export function useDocuments() {
  const { getAccessToken } = useAuth();

  return useQuery({
    queryKey: ["documents"],
    queryFn: () => fetchDocuments(getAccessToken),
  });
}

// --- Create documents ---

export interface DocumentCreatePayload {
  filename: string;
  title: string | null;
  authors: string | null;
  year: string | null;
  description: string | null;
  source_type: string | null;
  organization: string | null;
  country: string | null;
  evidence_type: string | null;
  date_relevance: string | null;
  url: string | null;
  language: string | null;
  ownership: string | null;
  tags: string | null;
}

async function createDocument(
  payload: DocumentCreatePayload,
  getAccessToken: () => Promise<string | null>
): Promise<DocumentApiResponse> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.detail || `Metadata save failed: ${response.status}`
    );
  }

  return response.json();
}

async function createDocuments(
  payloads: DocumentCreatePayload[],
  getAccessToken: () => Promise<string | null>
) {
  return Promise.all(
    payloads.map((payload) => createDocument(payload, getAccessToken))
  );
}

export function useSaveDocumentMetadata() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payloads: DocumentCreatePayload[]) =>
      createDocuments(payloads, getAccessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

// --- Check duplicates ---

export interface DuplicateCheckItem {
  title: string | null;
  filename: string | null;
  authors: string | null;
  country: string | null;
  year: string | null;
}

export interface DuplicateCheckResult {
  index: number;
  is_duplicate: boolean;
  matching_document_id: string | null;
  match_type: "exact" | "renamed" | null;
  matched_filename: string | null;
  matched_title: string | null;
}

async function checkDuplicates(
  items: DuplicateCheckItem[],
  getAccessToken: () => Promise<string | null>
): Promise<DuplicateCheckResult[]> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(
    `${EVIDENCE_BASE_API_URL}/api/evidence/documents/check-duplicates`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ items }),
    }
  );

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.detail || `Duplicate check failed: ${response.status}`
    );
  }

  const data: { results: DuplicateCheckResult[] } = await response.json();
  return data.results;
}

export function useCheckDuplicates() {
  const { getAccessToken } = useAuth();

  return useMutation({
    mutationFn: (items: DuplicateCheckItem[]) =>
      checkDuplicates(items, getAccessToken),
  });
}

// --- Update document ---

export interface DocumentUpdatePayload {
  filename?: string | null;
  title?: string | null;
  authors?: string | null;
  year?: string | null;
  description?: string | null;
  source_type?: string | null;
  organization?: string | null;
  country?: string | null;
  evidence_type?: string | null;
  date_relevance?: string | null;
  url?: string | null;
  language?: string | null;
  ownership?: string | null;
  tags?: string | null;
}

async function updateDocument(
  id: string,
  payload: DocumentUpdatePayload,
  getAccessToken: () => Promise<string | null>
) {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.detail || `Update failed: ${response.status}`
    );
  }

  return response.json();
}

export function useUpdateDocument() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: DocumentUpdatePayload & { id: string }) =>
      updateDocument(id, payload, getAccessToken),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["documents"] });
    },
  });
}

// --- Delete document ---

async function deleteDocument(
  id: string,
  getAccessToken: () => Promise<string | null>
): Promise<void> {
  const token = await getAccessToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${EVIDENCE_BASE_API_URL}/api/evidence/documents/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) throw new Error("Unauthorized");
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.detail || `Delete failed: ${response.status}`);
  }
}

export function useDeleteDocument() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDocument(id, getAccessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
